'use client';

import { Button } from '@common/components';
import { useMemo } from 'react';
import CountUp from 'react-countup';

import {
  CartBottomCTABase,
  CartBottomCTADesktop,
} from '@/app/donation/_components/CartBottomCTABase';
import { useGiftCart } from '@/app/donation/_hooks/useGiftCart';
import { calculateTaxBenefit } from '@/app/donation/_utils';
import { BREAKPOINTS, DONATION_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAuthStore } from '@/stores/AuthStore';

interface GiftCartBottomCTAProps {
  isHidden?: boolean;
  className?: string;
  desktopOnly?: boolean;
}

const calculateTotalBenefit = (
  donationAmount: number,
  giftBenefitAmount: number,
): number => {
  const taxBenefit = calculateTaxBenefit(donationAmount);

  return taxBenefit + giftBenefitAmount;
};

export const GiftCartBottomCTA = ({
  isHidden = false,
  className,
  desktopOnly = false,
}: GiftCartBottomCTAProps) => {
  const { push } = useCustomRouter();
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP, false);
  const isLogin = useAuthStore((state) => !!state.isLogin);
  const { items, totalCount, totalPrice } = useGiftCart();

  const handleClick = () => {
    push(DONATION_ROUTES.CART.pathname);
  };

  const cartItemCount = totalCount || items.length;

  const giftBenefitAmount = useMemo(
    () => items.reduce((sum, item) => sum + item.originItemTotalPrice, 0),
    [items],
  );

  const benefitAmount = useMemo(
    () => Math.floor(calculateTotalBenefit(totalPrice, giftBenefitAmount)),
    [totalPrice, giftBenefitAmount],
  );

  const shouldHide = !isLogin || cartItemCount === 0 || isHidden;

  const toastContent = (
    <p className="font-700 text-Gray-white typo-body3">
      <span className="text-O-400">
        총{' '}
        <CountUp
          preserveValue
          end={benefitAmount}
          duration={0.3}
          separator=","
        />
        원
      </span>{' '}
      혜택을 받을 수 있어요!
    </p>
  );

  // 데스크탑 레이아웃
  if (isDesktop) {
    return (
      <CartBottomCTADesktop
        isHidden={shouldHide}
        className={className}
        toastContent={toastContent}
      >
        <div className="gap-40 px-20 py-12 flex-center">
          {/* 기부 금액 */}
          <div className="flex items-center gap-8">
            <span className="font-400 text-Gray-800 typo-body3">기부 금액</span>
            <span className="font-800 text-Gray-900 typo-h4">
              <CountUp
                preserveValue
                end={totalPrice}
                duration={0.3}
                separator=","
              />
              원
            </span>
          </div>

          {/* 버튼 */}
          <Button
            dimensions="H56"
            theme="primary-fill"
            className="h-78 w-[335px] !rounded-large bg-O-400 hover:border-O-500 hover:bg-O-500"
            onClick={handleClick}
          >
            <span className="flex size-24 items-center justify-center rounded-pill bg-Gray-white">
              <span className="font-700 text-O-400 typo-caption1">
                {cartItemCount}
              </span>
            </span>
            <span className="font-700 text-Gray-white typo-h5">
              장바구니 보기
            </span>
          </Button>
        </div>
      </CartBottomCTADesktop>
    );
  }

  if (desktopOnly) {
    return null;
  }

  // 모바일 레이아웃
  return (
    <CartBottomCTABase
      isHidden={shouldHide}
      className={className}
      toastContent={toastContent}
    >
      <div className="flex items-center gap-12 px-20 py-12">
        {/* 기부 금액 */}
        <div className="mr-20 flex flex-1 flex-col items-start">
          <span className="font-400 text-Gray-800 typo-body4">기부 금액</span>
          <span className="font-700 text-Gray-black typo-body1">
            <CountUp
              preserveValue
              end={totalPrice}
              duration={0.3}
              separator=","
            />
            원
          </span>
        </div>

        {/* 버튼 */}
        <div className="flex min-w-150 flex-1 items-start justify-center">
          <Button
            dimensions="H56"
            theme="primary-fill"
            className="flex-1 bg-O-400 hover:border-O-500 hover:bg-O-500"
            onClick={handleClick}
          >
            <span className="flex size-20 items-center justify-center rounded-pill bg-Gray-white mobile:size-16">
              <span className="break-keep font-700 text-O-400 typo-caption1">
                {cartItemCount}
              </span>
            </span>
            <span className="break-keep font-700 text-Gray-white typo-body3 mobile:typo-body4">
              장바구니 보기
            </span>
          </Button>
        </div>
      </div>
    </CartBottomCTABase>
  );
};
